import { formatDateTime, formatNumber, formatPercent } from "../data/formatters";
import type { EventSnapshot, SourceStatus } from "../types";
import { MetricCard } from "../components/MetricCard";
import { Panel } from "../components/Panel";

export function QualityPage({ snapshot, status }: { snapshot: EventSnapshot; status: SourceStatus }) {
  const quality = snapshot.quality;
  const checks = [
    { label: "achieverCount 與資料列數一致", ok: quality.countMatches, detail: `${formatNumber(snapshot.achieverCount)} / ${formatNumber(quality.rawCount)}` },
    { label: "達標次序無重複", ok: quality.duplicateOrders.length === 0, detail: quality.duplicateOrders.length ? `${quality.duplicateOrders.length} 個重複值` : "通過" },
    { label: "達標次序無缺口", ok: quality.missingOrders.length === 0, detail: quality.missingOrders.length ? `${quality.missingOrders.length} 個缺口` : "通過" },
    { label: "性別代碼皆可辨識", ok: quality.unknownGenderCodes.length === 0, detail: quality.unknownGenderCodes.length ? quality.unknownGenderCodes.join("、") : "通過" },
    { label: "地點代碼皆可辨識", ok: quality.unknownLocationCodes.length === 0, detail: quality.unknownLocationCodes.length ? quality.unknownLocationCodes.join("、") : "通過" },
  ];

  return (
    <div className="page-stack">
      <div className="metric-grid metric-grid--four">
        <MetricCard label="原始資料列" value={formatNumber(quality.rawCount)} note="來源 achieverList" tone="blue" />
        <MetricCard label="有效資料列" value={formatNumber(quality.validCount)} note={formatPercent(quality.validCount / Math.max(1, quality.rawCount))} tone="mint" />
        <MetricCard label="非公開 userId" value={formatNumber(quality.privateUserCount)} note="每列分開保留，不合併" tone="yellow" />
        <MetricCard label="最近回應時間" value={status.responseTimeMs == null ? "—" : `${status.responseTimeMs.toLocaleString()} ms`} note={status.updateMessage} tone="pink" />
      </div>

      <div className="content-grid content-grid--wide-left">
        <Panel title="欄位完整度" eyebrow="FIELD COMPLETENESS">
          <div className="quality-bars">
            <QualityBar label="年齡缺值" count={quality.missingAge} total={quality.rawCount} color="#ffcf5c" />
            <QualityBar label="性別未知／未公開" count={quality.missingGender} total={quality.rawCount} color="#ff718b" />
            <QualityBar label="地點未知／未公開" count={quality.missingLocation} total={quality.rawCount} color="#8ea9ff" />
            <QualityBar label="海外" count={quality.overseasCount} total={quality.rawCount} color="#44d7b6" />
            <QualityBar label="年齡 0 或超出範圍" count={quality.invalidAgeCount} total={quality.rawCount} color="#d59cff" />
          </div>
        </Panel>
        <Panel title="契約檢查" eyebrow="CONTRACT CHECKS">
          <div className="check-list">
            {checks.map((check) => (
              <div key={check.label} className={check.ok ? "is-ok" : "is-warning"}>
                <span>{check.ok ? "✓" : "!"}</span><strong>{check.label}</strong><em>{check.detail}</em>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="來源與更新狀態" eyebrow="SOURCE STATUS">
        <div className="source-status">
          <dl>
            <div><dt>賽事鍵值</dt><dd>{snapshot.eventKey}</dd></div>
            <div><dt>官方資料彙整</dt><dd>{formatDateTime(snapshot.aggregateAt)}</dd></div>
            <div><dt>目前快照擷取</dt><dd>{formatDateTime(snapshot.fetchedAt)}</dd></div>
            <div><dt>最後 metadata 檢查</dt><dd>{status.lastCheckedAt ? formatDateTime(status.lastCheckedAt) : "—"}</dd></div>
            <div><dt>是否沿用舊快照</dt><dd>{status.usingPreviousSnapshot ? "是，更新失敗" : "否，目前為最新有效快照"}</dd></div>
            <div><dt>Schema 狀態</dt><dd>{status.updateMessage}</dd></div>
          </dl>
          <p>跑者資料只保存在目前瀏覽器分頁記憶體中；本機設定只保存最近開啟的賽事網址，不保存個人資料或歷史快照。</p>
        </div>
      </Panel>
    </div>
  );
}

function QualityBar({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const rate = count / Math.max(1, total);
  return (
    <div className="quality-bar">
      <div><strong>{label}</strong><span>{formatNumber(count)} / {formatPercent(rate)}</span></div>
      <div className="quality-bar__track"><span style={{ width: `${Math.max(rate * 100, count ? 1 : 0)}%`, background: color }} /></div>
    </div>
  );
}
